// @flow

import * as React from 'react';
import {BodyMono} from 'sail/Text';
import Group from 'sail/Group';
import type {Command, Param} from '../commands/resource';
import type {InputFlag} from '../../terminal/command';

export const white_space = /\s\s+/g;

// Return the tag and value of input given as data.
export function handleData(input: string): [string, string] {
  const data = input.split('=', 2);

  if (data.length !== 2) {
    return ['', ''];
  }

  const name = extractFromQuotes(data[0]);
  const value = extractFromQuotes(data[1]);

  // Note: this is explicitly returning a tuple
  return [name, value];
}

// Check if the name (data[0]) starts and ends with quotes (this might
// happen for data params that have square brackets in them). If it does,
// remove those quotes since we won't pass them into the API request
function extractFromQuotes(input: string): string {
  return (input.startsWith('"') && input.endsWith('"')) ||
    (input.startsWith("'") && input.endsWith("'"))
    ? input.slice(1, input.length - 1)
    : input;
}

function expandShorthand(name: string, paramConfig: Param): string {
  return (
    Object.entries(paramConfig).find(
      ([_, v]) =>
        typeof v === 'object' &&
        v != null &&
        v.hasOwnProperty('shorthand') &&
        v.shorthand === name,
    )?.[0] ?? name
  );
}

// Parses parameter/values list into an Object with key/value pairs
export function parseParams(
  rawFlags: Array<InputFlag>,
  paramConfig: Param,
  current_node: Command,
): {[string]: string} {
  const input = [...rawFlags];
  if (input.length === 0) {
    return {};
  }

  const params = {};
  let expand_count = 0;
  input.forEach(function(item) {
    const param = item.name;
    // I'm not sure if the following is needed. This function had a param
    // called require_valid that was used as a branch in an if statement:
    // `if (require_valid) { handleTag } else { getTagWord }
    // All usage of this function always passed require_valid = true so this
    // branch never actually hit
    // param = getTagWord(item.name, params_config);

    if (param === 'data' || param === '--data' || param === 'd') {
      const [tag, value] = handleData(item.value);
      if (tag) {
        params[tag] = value;
      }
    } else if (param === 'expand') {
      params[`expand[${expand_count}]`] = item.value;
      expand_count += 1;
    } else if (param.length === 1) {
      params[expandShorthand(param, paramConfig)] = item.value;
    } else if (param) {
      params[param] = item.value;
    } else {
      throw new Error(`Unsupported parameter: ${param}`);
    }
  });

  return params;
}

export function paramRequiresValue(
  paramName: string,
  currentNode: Command,
): boolean {
  return currentNode.params && currentNode.params[paramName]
    ? currentNode.params[paramName].no_value !== true
    : true;
}

export function parse(input: string): Array<string> {
  return input.split(/\s+/g);
}

export function normalizeCommand(input: string): string {
  return input
    .replace(/\\/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// Display the spinner while waiting.
export const Spinner = ({text = ''}: {text: string}) => {
  const FRAMES = '⣾⣽⣻⢿⡿⣟⣯⣷';
  const FPS = 12;

  const [frameIdx, setFrameIdx] = React.useState(0);
  const requestRef = React.useRef();
  const timeoutRef = React.useRef();

  React.useEffect(() => {
    const cycle = () => {
      timeoutRef.current = setTimeout(() => {
        setFrameIdx((state) => (state + 1) % FRAMES.length);
        requestRef.current = requestAnimationFrame(cycle);
      }, 1000 / FPS);
    };

    requestRef.current = requestAnimationFrame(cycle);

    return () => {
      if (requestRef && requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }

      if (timeoutRef && timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <BodyMono color="gray200">
      <Group spacing={2} direction="horizontal">
        <span style={{marginRight: 8}}>{FRAMES[frameIdx]}</span>
        {text}
      </Group>
    </BodyMono>
  );
};
